import React from 'react';
import { FaFileExport, FaExternalLinkAlt } from 'react-icons/fa';
import './RamBotResultsWidget.css';

// Groups search_runbooks' flat `results` array by client, preserving first-seen order (the
// backend already returns them ordered by ClientName, RunbookId).
function groupByClient(results) {
  const order = [];
  const byClient = new Map();
  for (const r of results || []) {
    const key = r.clientName || r.clientId || 'Unknown client';
    if (!byClient.has(key)) {
      byClient.set(key, []);
      order.push(key);
    }
    byClient.get(key).push(r);
  }
  return order.map((key) => [key, byClient.get(key)]);
}

// One suggestion per distinct system/object combo (already excludes custom objects -- the
// backend never includes them here), capped so the row doesn't overflow.
const MAX_SUGGESTIONS = 4;
function buildSuggestions(combos) {
  if (!combos || combos.length < 2) return [];
  return combos.slice(0, MAX_SUGGESTIONS).map((c) => (c.systemObject ? `${c.system} ${c.systemObject}` : c.system));
}

// No open/export affordances here on purpose (#2448) -- Michael: "I want people to ask you to
// tell them about a certain runbook, and then have the export json and view [there]". Viewing and
// exporting are gated behind an explicit get_runbook_details turn (RunbookDetailsWidget below);
// this first response is look-but-don't-click, with a hint steering toward that follow-up.
function SearchResultsWidget({ data, onSendSuggestion }) {
  const clientGroups = groupByClient(data.results);
  const examples = (data.results || []).filter((r) => r.description).slice(0, 3);
  const suggestions = buildSuggestions(data.distinctSystemObjectActions);

  if (clientGroups.length === 0) return null;

  return (
    <div className="ram-bot-widget">
      <div className="ram-bot-widget-section">
        {clientGroups.map(([clientName, runbooks]) => (
          <div key={clientName} className="ram-bot-widget-client-row">
            <span className="ram-bot-widget-client-name">{clientName}</span>
            <span className="ram-bot-widget-chip-list">
              {runbooks.map((r) => (
                <span key={r.runbookId} className="ram-bot-widget-chip">
                  {r.runbookId}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>

      {examples.length > 0 && (
        <div className="ram-bot-widget-section">
          <div className="ram-bot-widget-heading">Examples</div>
          {examples.map((r) => (
            <div key={r.runbookId} className="ram-bot-widget-example">
              <span className="ram-bot-widget-example-id">{r.runbookId} / {r.clientName}</span>
              <span className="ram-bot-widget-example-desc">{r.description}</span>
            </div>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="ram-bot-widget-section">
          <div className="ram-bot-widget-heading">Narrow it down</div>
          <div className="ram-bot-widget-chip-list">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="ram-bot-widget-suggestion"
                onClick={() => onSendSuggestion(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="ram-bot-widget-hint">Ask about a specific runbook (e.g. "tell me about RB1234") to view or export it.</div>
    </div>
  );
}

function RunbookDetailsWidget({ data, onOpenRunbook, onExportRunbook, exporting }) {
  const hasSystemObjects = data.systemObjects && data.systemObjects.length > 0;
  if (!hasSystemObjects) return null;

  return (
    <div className="ram-bot-widget">
      <div className="ram-bot-widget-section ram-bot-widget-actions">
        <button
          type="button"
          className="ram-bot-widget-export-btn"
          onClick={() => onOpenRunbook(data.runbookId, data.runbookGuid, data.clientId)}
          title={`View ${data.runbookId}`}
        >
          <FaExternalLinkAlt className="me-1" /> View {data.runbookId}
        </button>
        <button
          type="button"
          className="ram-bot-widget-export-btn"
          onClick={() => onExportRunbook(data.runbookId, data.runbookGuid)}
          disabled={exporting}
          title="Connector credentials are never included in the export"
        >
          <FaFileExport className="me-1" /> {exporting ? 'Exporting…' : 'Export JSON'}
        </button>
      </div>
      <div className="ram-bot-widget-section">
        <div className="ram-bot-widget-heading">Systems / objects / actions</div>
        <ul className="ram-bot-widget-list">
          {data.systemObjects.map((s) => (
            <li key={`${s.system}-${s.systemObject}-${s.action}`}>
              {s.system} / {s.systemObject}
              {s.isCustom && <span className="ram-bot-widget-custom-badge">custom</span>} / {s.action}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Renders the structured data alongside RamBot's prose answer -- which widget depends on which
// tool produced the data. View/export only exist on the get_runbook_details widget, by design
// (see SearchResultsWidget above) -- onOpenRunbook/onExportRunbook are unused on the
// search_runbooks path. onOpenRunbook's shape (runbookId, runbookGuid, clientId) stays generic so
// a later iteration can switch client context and open the runbook in the designer (CoryBot)
// without changing this component. onExportRunbook downloads the runbook's current saved
// definition as JSON -- same shape/safety as the designer's own Export Workflow (connector
// credentials are never present in it) -- via runbookId+runbookGuid.
function RamBotResultsWidget({ toolName, data, onOpenRunbook, onSendSuggestion, onExportRunbook, exporting }) {
  if (!data) return null;

  if (toolName === 'search_runbooks') {
    return <SearchResultsWidget data={data} onSendSuggestion={onSendSuggestion} />;
  }
  if (toolName === 'get_runbook_details') {
    return (
      <RunbookDetailsWidget
        data={data}
        onOpenRunbook={onOpenRunbook}
        onExportRunbook={onExportRunbook}
        exporting={exporting}
      />
    );
  }
  return null;
}

export default RamBotResultsWidget;
